/* eslint-disable */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Button,
  TextField,
  Grid,
  Divider,
  Snackbar,
  Alert,
  useTheme,
  IconButton,
  Tooltip,
} from '@mui/material';
import { motion } from 'framer-motion';
import {
  Person,
  Work,
  School,
  Build,
  Palette,
  ArrowForward,
  Description,
  BugReport,
  EmojiEvents,
  Translate,
  Assignment,
  Groups,
  Contacts,
  Article,
  Mic,
} from '@mui/icons-material';
import CVPreview from '../components/CVPreview';
import { generateCV } from '../services/cvService';
import VoiceRecorder from '../components/VoiceRecorder';

const GeneratePage = () => {
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    personalInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      location: '',
      title: '',
      summary: '', // Added professional summary
    },
    experience: '', // Natural language input
    education: '',  // Natural language input
    skills: '',     // Natural language input
    certifications: '', // Added certifications
    languages: '',      // Added languages
    projects: '',       // Added projects
    memberships: '',    // Added professional memberships
    references: '',     // Added references
    template: 'modern', // Default template
  });
  const [cvMarkdown, setCvMarkdown] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [testMessage, setTestMessage] = useState('');
  const [userStatus, setUserStatus] = useState({
    isPremium: false
  });

  const steps = [
    'Personal Information',
    'Professional Summary',
    'Work Experience',
    'Education',
    'Skills',
    'Additional Details',
    'Pick a Style',
    'View Your CV',
  ];

  const handleNext = () => {
    // Validate current step before proceeding
    if (activeStep === 0) {
      // Check if personal info is filled
      const { firstName, lastName, email, title } = formData.personalInfo;
      if (!firstName || !lastName || !email || !title) {
        setError('Please fill in at least first name, last name, email, and professional title.');
        return;
      }
    } else if (activeStep === 2 && !formData.experience) {
      setError('Please provide some information about your work experience.');
      return;
    } else if (activeStep === 3 && !formData.education) {
      setError('Please provide some information about your education.');
      return;
    } else if (activeStep === 4 && !formData.skills) {
      setError('Please provide at least a few skills.');
      return;
    }

    // Generate CV when moving to preview step
    if (activeStep === 6) {
      generateCVHandler();
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handlePersonalInfoChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      personalInfo: { ...prev.personalInfo, [name]: value },
    }));
  };

  const handleTextChange = (field) => (event) => {
    setFormData((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const generateCVHandler = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await generateCV(formData);
      setCvMarkdown(data.cv);
      setShowPreview(true);
    } catch (err) {
      console.error('Error generating CV:', err);
      setError(err.message || 'Failed to generate CV. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const templates = [
    { value: 'modern', label: 'Modern' },
    { value: 'classic', label: 'Classic' },
    { value: 'minimal', label: 'Minimal' },
    { value: 'creative', label: 'Creative' },
  ];

  // Custom StepIcon for a polished look
  const CustomStepIcon = (props) => {
    const { active, icon } = props;
    const icons = {
      1: <Person />,
      2: <Work />,
      3: <School />,
      4: <Build />,
      5: <Palette />,
      6: <Description />,
    };
    return (
      <Box
        sx={{
          color: active ? 'primary.main' : 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.5rem',
        }}
      >
        {icons[icon]}
      </Box>
    );
  };

  // Development-only function to test OpenAI connection
  const testOpenAIConnection = async () => {
    if (process.env.NODE_ENV !== 'development') return;
    
    try {
      setTestMessage('Testing OpenAI connection...');
      const { default: OpenAI } = await import('openai');
      
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      console.log("Testing with API key (first 10 chars):", apiKey ? apiKey.substring(0, 10) + "..." : "undefined");
      
      if (!apiKey) {
        setTestMessage('API key is missing! Check .env.development.local file');
        return;
      }
      
      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });
      
      const response = await openai.chat.completions.create({
        model: "gpt-4o",
        messages: [
          {
            role: "user",
            content: "Say hello!"
          }
        ],
        temperature: 0.7,
        max_tokens: 10,
      });
      
      setTestMessage(`Success! OpenAI responded with: "${response.choices[0].message.content}"`);
    } catch (err) {
      console.error("OpenAI test failed:", err);
      setTestMessage(`Error: ${err.message}`);
    }
  };

  // Handler for voice transcription
  const handleVoiceInput = (fieldName, isPersonalInfo = false) => (text) => {
    if (isPersonalInfo) {
      setFormData(prev => ({
        ...prev,
        personalInfo: {
          ...prev.personalInfo,
          [fieldName]: text
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [fieldName]: text
      }));
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" gutterBottom>
              Personal Information
              <Tooltip title="Record your information">
                <IconButton 
                  color="primary" 
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                >
                  <Mic />
                </IconButton>
              </Tooltip>
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="First Name"
                    name="firstName"
                    value={formData.personalInfo.firstName}
                    onChange={handlePersonalInfoChange}
                    variant="outlined"
                    required
                  />
                  <VoiceRecorder onTranscriptionComplete={handleVoiceInput('firstName', true)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    name="lastName"
                    value={formData.personalInfo.lastName}
                    onChange={handlePersonalInfoChange}
                    variant="outlined"
                    required
                  />
                  <VoiceRecorder onTranscriptionComplete={handleVoiceInput('lastName', true)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={formData.personalInfo.email}
                    onChange={handlePersonalInfoChange}
                    variant="outlined"
                    required
                  />
                  <VoiceRecorder onTranscriptionComplete={handleVoiceInput('email', true)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Phone"
                    name="phone"
                    value={formData.personalInfo.phone}
                    onChange={handlePersonalInfoChange}
                    variant="outlined"
                  />
                  <VoiceRecorder onTranscriptionComplete={handleVoiceInput('phone', true)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Location"
                    name="location"
                    value={formData.personalInfo.location}
                    onChange={handlePersonalInfoChange}
                    variant="outlined"
                    placeholder="e.g., New York, NY"
                  />
                  <VoiceRecorder onTranscriptionComplete={handleVoiceInput('location', true)} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    fullWidth
                    label="Professional Title"
                    name="title"
                    value={formData.personalInfo.title}
                    onChange={handlePersonalInfoChange}
                    variant="outlined"
                    placeholder="e.g., Senior Software Engineer"
                    required
                  />
                  <VoiceRecorder onTranscriptionComplete={handleVoiceInput('title', true)} />
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      case 1:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" gutterBottom>
              Professional Summary
              <Tooltip title="Record your summary">
                <IconButton 
                  color="primary" 
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                >
                  <Mic />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Provide a brief overview of your professional background, expertise, and career goals.
              <br />
              <em>Example: "Enthusiastic Data Scientist with a background in project engineering. Possess strong analytical skills and experience in AI, RAG, Python, and SQL."</em>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                name="summary"
                value={formData.personalInfo.summary}
                onChange={handlePersonalInfoChange}
                variant="outlined"
                placeholder="Describe your professional summary here..."
              />
              <Box sx={{ mt: 1, ml: 1 }}>
                <VoiceRecorder onTranscriptionComplete={handleVoiceInput('summary', true)} />
              </Box>
            </Box>
          </Box>
        );
      case 2:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" gutterBottom>
              Work Experience
              <Tooltip title="Record your work experience">
                <IconButton 
                  color="primary" 
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                >
                  <Mic />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tell us about your professional journey in your own words. Our AI will format it into a professional CV layout.
              <br />
              <em>Example: "Data Scientist at BSC Global (2024-Present): Utilized AI and Python to develop predictive models. Project Engineer at ETA Operations (2021-2024): Managed projects in the mining sector."</em>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={formData.experience}
                onChange={handleTextChange('experience')}
                variant="outlined"
                placeholder="Describe your work experience here..."
                required
              />
              <Box sx={{ mt: 1, ml: 1 }}>
                <VoiceRecorder onTranscriptionComplete={handleVoiceInput('experience')} />
              </Box>
            </Box>
          </Box>
        );
      case 3:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" gutterBottom>
              Education
              <Tooltip title="Record your education">
                <IconButton 
                  color="primary" 
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                >
                  <Mic />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Tell us about your degrees, certifications, and relevant coursework. Our AI will organize it for your CV.
              <br />
              <em>Example: "Ph.D. in Data Analytics (2022-Present), Master's in Computer Science (2020-2022), Bachelor's in Mechanical Engineering (2017-2020)"</em>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                rows={6}
                value={formData.education}
                onChange={handleTextChange('education')}
                variant="outlined"
                placeholder="Describe your education here..."
                required
              />
              <Box sx={{ mt: 1, ml: 1 }}>
                <VoiceRecorder onTranscriptionComplete={handleVoiceInput('education')} />
              </Box>
            </Box>
          </Box>
        );
      case 4:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" gutterBottom>
              Skills
              <Tooltip title="Record your skills">
                <IconButton 
                  color="primary" 
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                >
                  <Mic />
                </IconButton>
              </Tooltip>
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Mention your key skills, separated by commas or line breaks. Our AI will format them for your CV.
              <br />
              <em>Example: "Artificial Intelligence, RAG, Python, SQL, Data Visualization"</em>
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                multiline
                rows={4}
                value={formData.skills}
                onChange={handleTextChange('skills')}
                variant="outlined"
                placeholder="List your skills here..."
                required
              />
              <Box sx={{ mt: 1, ml: 1 }}>
                <VoiceRecorder onTranscriptionComplete={handleVoiceInput('skills')} />
              </Box>
            </Box>
          </Box>
        );
      case 5:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" gutterBottom>
              Additional Information
              <Tooltip title="Record additional information">
                <IconButton 
                  color="primary" 
                  size="small"
                  sx={{ ml: 1, verticalAlign: 'middle' }}
                >
                  <Mic />
                </IconButton>
              </Tooltip>
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Certifications
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.certifications}
                    onChange={handleTextChange('certifications')}
                    variant="outlined"
                    placeholder="List any relevant certifications..."
                  />
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <VoiceRecorder onTranscriptionComplete={handleVoiceInput('certifications')} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Languages
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.languages}
                    onChange={handleTextChange('languages')}
                    variant="outlined"
                    placeholder="List languages you speak..."
                  />
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <VoiceRecorder onTranscriptionComplete={handleVoiceInput('languages')} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Projects
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.projects}
                    onChange={handleTextChange('projects')}
                    variant="outlined"
                    placeholder="Describe key projects you've worked on..."
                  />
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <VoiceRecorder onTranscriptionComplete={handleVoiceInput('projects')} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Professional Memberships
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={formData.memberships}
                    onChange={handleTextChange('memberships')}
                    variant="outlined"
                    placeholder="List any professional organizations you belong to..."
                  />
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <VoiceRecorder onTranscriptionComplete={handleVoiceInput('memberships')} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  References
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    value={formData.references}
                    onChange={handleTextChange('references')}
                    variant="outlined"
                    placeholder="Add references or simply state 'Available upon request'..."
                  />
                  <Box sx={{ mt: 1, ml: 1 }}>
                    <VoiceRecorder onTranscriptionComplete={handleVoiceInput('references')} />
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        );
      case 6:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Typography variant="h6" gutterBottom>
              Choose a Template
            </Typography>
            <Grid container spacing={3}>
              {templates.map((template) => (
                <Grid item xs={12} sm={6} md={3} key={template.value}>
                  <Paper
                    elevation={formData.template === template.value ? 8 : 1}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: formData.template === template.value ? 2 : 0,
                      borderColor: 'primary.main',
                      '&:hover': { elevation: 4 },
                    }}
                    onClick={() => setFormData((prev) => ({ ...prev, template: template.value }))}
                  >
                    <Typography variant="h6" align="center">
                      {template.label}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      case 7:
        return (
          <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Your CV Preview
            </Typography>
            <Divider sx={{ mb: 3 }} />
            <CVPreview 
              cvMarkdown={cvMarkdown} 
              loading={loading} 
              template={formData.template}
              error={error}
              onRetry={() => {
                setError('');
                generateCVHandler();
              }}
              isPremiumUser={userStatus.isPremium}
            />
          </Box>
        );
      default:
        return <Typography>Step content in development</Typography>;
    }
  };

  // Update the premium status effect
  useEffect(() => {
    // Check if user has premium status in localStorage
    const storedPremiumStatus = localStorage.getItem('userPremiumStatus');
    if (storedPremiumStatus === 'true') {
      setUserStatus(prev => ({
        ...prev,
        isPremium: true
      }));
    }
  }, []);

  return (
    <Container
      maxWidth="lg"
      sx={{
        py: 8,
        background: 'linear-gradient(135deg, #f5f7fa, #e4e9f0)',
      }}
    >
      <Typography variant="h3" align="center" gutterBottom>
        Create Your CV with AI
      </Typography>
      
      {testMessage && (
        <Alert 
          severity={testMessage.includes('Success') ? 'success' : 'info'} 
          sx={{ mb: 2 }}
          onClose={() => setTestMessage('')}
        >
          {testMessage}
        </Alert>
      )}
      
      <Typography variant="subtitle1" align="center" color="text.secondary" sx={{ mb: 6 }}>
        Just tell us about yourself in your own words — our AI will handle the rest.
      </Typography>

      <Stepper 
        activeStep={activeStep} 
        alternativeLabel 
        sx={{ 
          mb: 6,
          [theme.breakpoints.down('md')]: {
            display: 'none'
          }
        }}
      >
        {steps.map((label, index) => (
          <Step key={label}>
            <StepLabel StepIconComponent={CustomStepIcon} icon={index + 1}>
              {label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Mobile stepper label */}
      <Box 
        sx={{ 
          display: 'none',
          [theme.breakpoints.down('md')]: {
            display: 'block',
            mb: 4,
            textAlign: 'center'
          }
        }}
      >
        <Typography variant="h5" fontWeight="bold" color="primary">
          Step {activeStep + 1}: {steps[activeStep]}
        </Typography>
      </Box>

      <Paper sx={{ p: 4, mb: 4, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        {renderStepContent(activeStep)}
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={activeStep === 0}
          sx={{ px: 4, py: 1.5 }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleNext}
          sx={{ px: 4, py: 1.5 }}
          disabled={loading || (activeStep === steps.length - 1)}
          endIcon={activeStep < steps.length - 1 ? <ArrowForward /> : null}
        >
          {activeStep === steps.length - 2 ? 'Generate CV' : 'Next'}
        </Button>
      </Box>

      {/* Error message */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default GeneratePage;